import PropTypes from 'prop-types';
import { useTranslation } from '@eti/providers';
import { price as priceUtil } from '@eti/utils';
import { productIds } from '@eti/products';
import { Text } from '@etg/wings';
import { ScreenReaderPrice } from '@eti/components';
import { getExtraProductsTotal, sortProductsByAlpabethicOrder } from '../utils';
import { CartRow, Price } from '../../CartComponents';
import CartToggleableRow from '../../CartToggleableRow';
import CartBreakdownRow from '../../CartBreakdownRow';

const CartExtraProducts = ({ bundles = [], extraProducts = [] }) => {
  const { t } = useTranslation();
  const extraProductsTotal = getExtraProductsTotal([...extraProducts, ...bundles]);
  const products = sortProductsByAlpabethicOrder(extraProducts);

  const getProductPriceBreakdownData = (price, productMarkup) => {
    return productMarkup
      ? [
          { label: t('SeatMap.PriceInformation.Markup'), value: productMarkup },
          {
            label: t('SeatMap.PriceInformation.ProviderCost'),
            value: price - productMarkup,
          },
        ]
      : null;
  };
  const shouldRenderSection = extraProducts.length > 0 || bundles.length > 0;

  return (
    <>
      {shouldRenderSection && (
        <div data-testid="cart-extra-products">
          <CartRow>
            <Text as="p" variant="16-bold">
              {t('Cart.ExtraServices.Label')}
            </Text>
            <Text aria-hidden as="p" data-testid="extra-products-totalPrice" variant="16-bold">
              {priceUtil.formatPrice(extraProductsTotal)}
            </Text>
            <ScreenReaderPrice price={priceUtil.formatPrice(extraProductsTotal)} />
          </CartRow>
          {bundles.length > 0 &&
            bundles.map((bundle) => (
              <CartToggleableRow
                data={bundle.products}
                dataTestId={`cart-product-${bundle.id}`}
                key={bundle.id}
                price={bundle.price}
                title={bundle.title}
              />
            ))}
          {products.map(({ id, markup, price, title, availability }) => {
            if (markup > 0) {
              return (
                <CartToggleableRow
                  data={getProductPriceBreakdownData(price, markup)}
                  dataTestId={`cart-product-${productIds[Number(id)]}`}
                  key={id}
                  price={price}
                  title={title}
                />
              );
            }

            if (availability && availability.hasAvailability) {
              return (
                <CartBreakdownRow
                  availability={availability}
                  dataTestId={`cart-product-${productIds[Number(id)]}`}
                  key={id}
                  price={price}
                  title={title}
                />
              );
            }

            return (
              <CartRow data-testid={`cart-product-${productIds[Number(id)]}`} key={id}>
                <Text>{title}</Text>
                <Price aria-hidden data-testid={`cart-product-${productIds[Number(id)]}-price`}>
                  {priceUtil.formatPrice(price)}
                </Price>
                <ScreenReaderPrice price={priceUtil.formatPrice(price)} />
              </CartRow>
            );
          })}
        </div>
      )}
    </>
  );
};

CartExtraProducts.propTypes = {
  bundles: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      optionId: PropTypes.string,
      price: PropTypes.number,
      products: PropTypes.arrayOf(PropTypes.object),
      title: PropTypes.string,
    }),
  ),
  extraProducts: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      markup: PropTypes.number,
      name: PropTypes.string,
      numberOfUnits: PropTypes.number,
      price: PropTypes.number,
      title: PropTypes.string,
      travelers: PropTypes.array,
    }),
  ),
};

export default CartExtraProducts;
